import excludeVariablesFromRoot from '@mui/material/styles/excludeVariablesFromRoot';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from 'react-query';
import { queryCreate, queryDelete, queryGet, queryUpdate } from './fetch';

export enum IncentivePaymentStatus {
  REQUESTED = 'REQUESTED',
  PAYED = 'PAYED',
  CANCELLED = 'CANCELLED',
  ERROR = 'ERROR',
}

export interface IncentivePayment {
  id: string;
  status: IncentivePaymentStatus;
  participantId: string;
  incentiveMethod?: string;
  incentive?: number;
  currency?: string;
  countryKey?: string;
  locale?: string;

  incentiveText?: string;
  sessionId?: string;
  participationId?: string;
  orderId?: string;
  eurAmount?: number;
  payedAt?: Date;
  payedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export const useIncentivePaymentForParticipation = (id?: string) => {
  return useQuery<IncentivePayment>(
    ['participations', id, 'incentive-payment'],
    queryGet(`/v2/participations/${id}/incentive-payment`),
    { enabled: !!id },
  );
};

const useMutationOptions = <T>(
  options?: UseMutationOptions<IncentivePayment, any, T>,
): UseMutationOptions<IncentivePayment, any, T> => {
  const queryClient = useQueryClient();
  return {
    ...options,
    onSuccess: (data, variables, context) => {
      if (data.participationId) {
        queryClient.invalidateQueries([
          'participations',
          data.participationId,
          'incentive-payment',
        ]);
      }
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  };
};

export const usePayIncentive = (
  id?: string,
  options?: UseMutationOptions<IncentivePayment, any, void>,
) => {
  const mutationOptions = useMutationOptions(options);
  return useMutation<IncentivePayment, any, void>(
    queryCreate(`/v2/participations/${id}/incentive-payments`),
    mutationOptions,
  );
};

export const useRemoveParticipationIncentivePayment = (
  id?: string,
  options?: UseMutationOptions<IncentivePayment, any, void>,
) => {
  const mutationOptions = useMutationOptions(options);
  return useMutation<IncentivePayment, any, void>(
    queryDelete(`/v2/participations/${id}/incentive-payment`),
    mutationOptions,
  );
};

export const useMarkAsPayed = (
  id?: string,
  options?: UseMutationOptions<IncentivePayment, any, void>,
) => {
  const mutationOptions = useMutationOptions(options);
  return useMutation<IncentivePayment, any, void>(
    queryUpdate(`/v2/incentive-payments/${id}/payed`),
    mutationOptions,
  );
};

export const useMarkAsRequested = (
  id?: string,
  options?: UseMutationOptions<IncentivePayment, any, void>,
) => {
  const mutationOptions = useMutationOptions(options);
  return useMutation<IncentivePayment, any, void>(
    queryUpdate(`/v2/incentive-payments/${id}/requested`),
    mutationOptions,
  );
};

interface UpdateIncentivePayementDto {
  currency: string;
  incentive: number;
  countryKey: string;
}

export const useUpdateIncentivePayment = (
  id?: string,
  options?: UseMutationOptions<
    IncentivePayment,
    any,
    UpdateIncentivePayementDto
  >,
) => {
  const mutationOptions = useMutationOptions(options);
  return useMutation<IncentivePayment, any, UpdateIncentivePayementDto>(
    queryUpdate(`/v2/incentive-payments/${id}`),
    mutationOptions,
  );
};
